import axios from 'axios'

// 创建axios实例
const http = axios.create({
  baseURL: 'https://ai.carbonneutrality-group.com/carbonInventory',
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
})

// 请求拦截器
http.interceptors.request.use(
  (config) => {
    // 在发送请求之前做些什么
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // 添加时间戳防止缓存
    if (config.method === 'get') {
      config.params = {
        ...config.params,
        _t: Date.now()
      }
    }
    config.headers['accept-language'] = "zh-CN"
    return config
  },
  (error) => {
    // 对请求错误做些什么
    console.error('❌ 请求拦截器错误:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
http.interceptors.response.use(
  (response) => {
    
    // 统一处理响应格式
    if (response.data && response.data.code !== undefined) {
      if (response.data.code === 200) {
        return response
      } else {
        // 业务逻辑错误
        const error = new Error(response.data.message || '请求失败')
        error.code = response
        error.data = response
        return Promise.reject(error)
      }
    }
    
    return response.data
  },
  (error) => {
    // 对响应错误做点什么
    console.error('❌ 响应拦截器错误:', error)
    
    if (error.response) {
      // 服务器返回错误状态码
      const { status, data } = error.response
      
      switch (status) {
        case 401:
          // 未授权，清除token并跳转到登录页
          localStorage.removeItem('token')
          window.location.href = '/login'
          break
        case 403:
          // 禁止访问
          console.error('权限不足，请联系管理员')
          break
        case 404:
          console.error('请求的资源不存在')
          break
        case 500:
          console.error('服务器内部错误')
          break
        default:
          console.error(`服务器错误: ${status}`)
      }
      
      const errorMessage = data?.message || `请求失败: ${status}`
      const customError = new Error(errorMessage)
      customError.code = status
      customError.data = data
      return Promise.reject(customError)
    } else if (error.request) {
      // 请求已发出但没有收到响应
      console.error('网络错误，请检查网络连接')
      const networkError = new Error('网络错误，请检查网络连接')
      networkError.code = 'NETWORK_ERROR'
      return Promise.reject(networkError)
    } else {
      // 其他错误
      console.error('请求配置错误:', error.message)
      return Promise.reject(error)
    }
  }
)

// 添加取消请求的功能
const pendingRequests = new Map()

// 生成请求的唯一标识
const generateReqKey = (config) => {
  const { method, url, params, data } = config
  return [method, url, JSON.stringify(params), JSON.stringify(data)].join('&')
}

// 添加请求到pending列表
const addPendingRequest = (config) => {
  const requestKey = generateReqKey(config)
  config.cancelToken = config.cancelToken || new axios.CancelToken((cancel) => {
    if (!pendingRequests.has(requestKey)) {
      pendingRequests.set(requestKey, cancel)
    }
  })
}

// 移除pending的请求
const removePendingRequest = (config) => {
  const requestKey = generateReqKey(config)
  if (pendingRequests.has(requestKey)) {
    const cancel = pendingRequests.get(requestKey)
    cancel(requestKey)
    pendingRequests.delete(requestKey)
  }
}

// 清空所有pending的请求
export const clearPendingRequests = () => {
  pendingRequests.forEach((cancel, key) => {
    cancel(key)
  })
  pendingRequests.clear()
}

// 添加取消重复请求的拦截器
http.interceptors.request.use(
  (config) => {
    removePendingRequest(config) // 在请求开始前，取消重复请求
    addPendingRequest(config) // 将当前请求添加到pending中
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

http.interceptors.response.use(
  (response) => {
    removePendingRequest(response.config) // 在请求结束后，移除本次请求
    return response
  },
  (error) => {
    removePendingRequest(error.config || {}) // 在请求失败时，移除本次请求
    if (axios.isCancel(error)) {
      console.log('已取消的重复请求:', error.message)
      return Promise.reject(new Error('请求被取消'))
    }
    return Promise.reject(error)
  }
)

// 导出常用的请求方法
export const get = (url, params = {}, config = {}) => {
  return http.get(url, { ...config, params })
}

export const post = (url, data = {}, config = {}) => {
  return http.post(url, data, config)
}

export const put = (url, data = {}, config = {}) => {
  return http.put(url, data, config)
}

export const del = (url, params = {}, config = {}) => {
  return http.delete(url, { ...config, params })
}

// 文件上传
export const upload = (url, formData, config = {}) => {
  return http.post(url, formData, {
    ...config,
    headers: {
      'Content-Type': 'multipart/form-data',
      ...config.headers
    }
  })
}

// 文件下载
export const download = (url, params = {}, config = {}) => {
  return http.get(url, {
    ...config,
    params,
    responseType: 'blob'
  })
}

export default http